import * as React from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { Text } from '../../../JRCTypo/JRCTypo';
import { WrappedFieldInputProps, WrappedFieldProps } from 'redux-form';
import { JRCTooltipProps } from 'components/JRCTooltip/JRCTooltip';
import { Colors } from 'styles/theme';
import { DataCy } from '../../../../types/dataAttributes';
import FormLabel, { JRCFormLabelProps } from '../../Common/FormLabel';
import FormHelper from '../../Common/FormHelper';
import FormMessageFeedback from '../../Common/FormMessageFeedback';

/**
 * Props used for all inputs
 *
 * @extends FormInputProps @see FormInputProps @see React.ComponentPropsWithoutRef
 * @extends ExternalFormFieldRenderProps @see ExternalFormFieldRenderProps
 *
 * @member name redux form name
 */
export interface JRCWritableFormInputProps extends DataCy, FormInputProps, ExternalFormFieldRenderProps {
    name: string;
}

/**
 * Basic input props, wihout the possibility to override the type
 */
export interface FormInputProps extends Omit<React.ComponentPropsWithoutRef<'input'>, 'type'> {}

/**
 * Form field render props
 *
 * @extends WrappedFieldProps @see WrappedFieldProps
 * @extends ExternalFormFieldRenderProps @see ExternalFormFieldRenderProps
 * @extends JRCFormLabelProps @see JRCFormLabelProps
 *
 * @member renderInputFct, render function, two parameters are passed,
 *         the redux form input object containing its state and a computed
 *         input state based on the validation hook
 */
export interface FormFieldRenderProps extends WrappedFieldProps, ExternalFormFieldRenderProps, JRCFormLabelProps {
    renderInputFct: (input: WrappedFieldInputProps, inputState: InputState) => JSX.Element;
}

/**
 * Form field render props that might be provided outside of this library
 * @member label, label
 * @member description, description, part of the html label
 * @member helper, tooltip, part of the html label
 * @member className styled components className
 */
export interface ExternalFormFieldRenderProps {
    label?: string;
    description?: string;
    helper?: Omit<JRCTooltipProps, 'children'>;
    className?: string;
    selected?: boolean;
    hideLabel?: boolean;
}

/**
 * Input state, if both booleans are false, the input is considered untouched or empty
 * @member error, the input is error regarding the validate hook
 * @member valid, the input is valid regarding the validate hook
 */
export type InputState = DataCy & {
    error?: boolean;
    valid?: boolean;
    readOnly?: boolean;
};

const StyledFieldWrapper = styled.fieldset`
    margin: 12px 0;
`;

export const StyledDescription = styled.div.attrs(() => ({ size: 's' }))`
    ${Text};
    color: ${(props) => props.theme.color.grey4};
`;

/**
 * Render of a default input redux form field
 * This render is used for all kin of inputs.
 * @params props FormFieldRenderProps
 * @returns JSX.Element
 * @deprecated Build component compatible with react-hook-form and using the field renderer from Input/Common instead
 */
const JRCFormFieldRender = (props: FormFieldRenderProps) => {
    const inputState = {
        error: !!props.meta.touched && !!props.meta.error && !!props.input.value,
        valid: !!props.meta.touched && !props.meta.error && props.input.value !== '',
    };

    const [helperColor, setHelperColor] = useState<Colors>('grey3');
    const colorHelperIcon = (event: boolean) => setHelperColor(event ? 'yellow' : 'grey3');

    return (
        <StyledFieldWrapper className={props.className} aria-label={props.label}>
            {!props.hideLabel && props.label && (
                <FormLabel htmlFor={props.htmlFor} labelIsLegend={props.labelIsLegend}>
                    {props.label}
                    {props.helper && <FormHelper tooltip={props.helper} color={helperColor} />}
                    {props.description && <StyledDescription>{props.description}</StyledDescription>}
                </FormLabel>
            )}
            <div onFocus={() => colorHelperIcon(true)} onBlur={() => colorHelperIcon(false)}>
                {props.renderInputFct(props.input, inputState)}
                {(props.meta.error || props.meta.warning) && (
                    <FormMessageFeedback error={props.meta.error} warning={props.meta.warning} />
                )}
            </div>
        </StyledFieldWrapper>
    );
};

export default JRCFormFieldRender;
